import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import {
  getDistributionAnalytics,
  exportDistributionAnalytics,
  getWarehouses,
  getInventoryKnownCities,
} from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Download, RefreshCw, MapPin, Warehouse as WarehouseIcon,
  Users, Calendar, BarChart3, Filter, Package,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  state_head: 'State Head',
  asm: 'ASM',
  tl: 'Team Lead',
  fse: 'FSE',
  merchant: 'Merchant',
  warehouse: 'Warehouse',
  scrap_bin: 'Scrap Bin',
  return_queue: 'Return Queue',
};

const roleLabel = (v) => ROLE_LABELS[v] || v || '(unknown)';

// Simple horizontal-bar visual for a data column
const MiniBar = ({ value, max, colorClass }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-full bg-slate-100 rounded h-1.5 overflow-hidden">
      <div
        className={`h-full ${colorClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const DistributionAnalytics = ({ user, setUser }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Filters
  const [startDate, setStartDate] = useState(isoDaysAgo(90));
  const [endDate, setEndDate] = useState(isoToday());
  const [city, setCity] = useState('all');
  const [warehouseId, setWarehouseId] = useState('all');
  const [toHolderType, setToHolderType] = useState('all');
  const [movementType, setMovementType] = useState('all');
  const [groupBy, setGroupBy] = useState('day');
  const [pivotDim, setPivotDim] = useState('city'); // 'city' | 'role'

  // Filter options
  const [warehouses, setWarehouses] = useState([]);
  const [cities, setCities] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (city && city !== 'all') params.city = city;
      if (warehouseId && warehouseId !== 'all') params.source_warehouse_id = warehouseId;
      if (toHolderType && toHolderType !== 'all') params.to_holder_type = toHolderType;
      if (movementType && movementType !== 'all') params.movement_type = movementType;
      const r = await getDistributionAnalytics(params);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, city, warehouseId, toHolderType, movementType, groupBy]);

  useEffect(() => {
    (async () => {
      try {
        const [wRes, cRes] = await Promise.all([getWarehouses(), getInventoryKnownCities()]);
        setWarehouses(wRes.data || []);
        setCities((cRes.data && cRes.data.cities) || []);
      } catch {
        /* silent */
      }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (city && city !== 'all') params.city = city;
      if (warehouseId && warehouseId !== 'all') params.source_warehouse_id = warehouseId;
      if (toHolderType && toHolderType !== 'all') params.to_holder_type = toHolderType;
      if (movementType && movementType !== 'all') params.movement_type = movementType;
      const r = await exportDistributionAnalytics(params);
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `distribution_analytics_${startDate}_to_${endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  const totals = data?.totals || {};
  const byCity = data?.by_city || [];
  const byWarehouse = data?.by_warehouse || [];
  const byRole = data?.by_role || [];
  const byItemType = data?.by_item_type || [];
  const byTime = data?.by_time || [];
  const pivotTypeCity = data?.pivot_type_city || {};
  const pivotTypeRole = data?.pivot_type_role || {};
  const rows = data?.rows || [];
  const rowsTotal = data?.rows_total || 0;

  const maxCityQty = Math.max(1, ...byCity.map((r) => r.quantity));
  const maxWhQty = Math.max(1, ...byWarehouse.map((r) => r.quantity));
  const maxRoleQty = Math.max(1, ...byRole.map((r) => r.quantity));
  const maxItemTypeQty = Math.max(1, ...byItemType.map((r) => r.quantity));
  const maxTimeQty = Math.max(1, ...byTime.map((r) => r.quantity));

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="distribution-analytics-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-indigo-600" />
              Distribution Analytics
            </h1>
            <p className="text-sm text-slate-500">
              Movement volume by city, warehouse, target role and time — with CSV export.
            </p>
          </div>
          <Button
            onClick={exportCsv}
            variant="outline"
            data-testid="export-csv-btn"
            disabled={loading}
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
        </div>

        {/* FILTER BAR */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="filter-start-date"
                />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="filter-end-date"
                />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="filter-city"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {cities.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Source Warehouse</Label>
                <Select value={warehouseId} onValueChange={setWarehouseId}>
                  <SelectTrigger data-testid="filter-warehouse"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Target Role</Label>
                <Select value={toHolderType} onValueChange={setToHolderType}>
                  <SelectTrigger data-testid="filter-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="state_head">State Head</SelectItem>
                    <SelectItem value="asm">ASM</SelectItem>
                    <SelectItem value="tl">Team Lead</SelectItem>
                    <SelectItem value="fse">FSE</SelectItem>
                    <SelectItem value="merchant">Merchant</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Movement</Label>
                <Select value={movementType} onValueChange={setMovementType}>
                  <SelectTrigger data-testid="filter-movement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Transfer + Deploy</SelectItem>
                    <SelectItem value="transfer">Transfer only</SelectItem>
                    <SelectItem value="deploy">Deploy only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Group Time By</Label>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger data-testid="filter-groupby"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={load}
                  variant="outline"
                  className="w-full"
                  data-testid="apply-filters-btn"
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Apply
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="kpi-movements">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500">Total Movements</p>
              <p className="text-2xl font-bold text-indigo-700">{totals.movements || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="kpi-quantity">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500">Total Quantity</p>
              <p className="text-2xl font-bold text-purple-700">{totals.quantity || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="kpi-unique-items">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500">Unique Items / Batches</p>
              <p className="text-2xl font-bold text-blue-700">{totals.unique_items || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="kpi-recipients">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500">Unique Recipients</p>
              <p className="text-2xl font-bold text-teal-700">{totals.unique_recipients || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* BREAKDOWNS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* BY CITY */}
          <Card data-testid="breakdown-city">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4 text-rose-600" />
                By City
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-medium">City</th>
                      <th className="text-right p-2 text-xs font-medium">Movements</th>
                      <th className="text-right p-2 text-xs font-medium">Quantity</th>
                      <th className="p-2 w-24 text-xs font-medium">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCity.map((r) => (
                      <tr key={r.city} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-medium">{r.city}</td>
                        <td className="p-2 text-right">{r.movements}</td>
                        <td className="p-2 text-right font-semibold">{r.quantity}</td>
                        <td className="p-2"><MiniBar value={r.quantity} max={maxCityQty} colorClass="bg-rose-500" /></td>
                      </tr>
                    ))}
                    {byCity.length === 0 && (
                      <tr><td colSpan="4" className="p-6 text-center text-slate-500">{loading ? 'Loading...' : 'No data'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* BY WAREHOUSE */}
          <Card data-testid="breakdown-warehouse">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <WarehouseIcon className="h-4 w-4 text-amber-600" />
                By Source Warehouse
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-medium">Warehouse</th>
                      <th className="text-right p-2 text-xs font-medium">Movements</th>
                      <th className="text-right p-2 text-xs font-medium">Quantity</th>
                      <th className="p-2 w-24 text-xs font-medium">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byWarehouse.map((r) => (
                      <tr key={r.source_warehouse_id} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-medium">{r.source_warehouse_name}</td>
                        <td className="p-2 text-right">{r.movements}</td>
                        <td className="p-2 text-right font-semibold">{r.quantity}</td>
                        <td className="p-2"><MiniBar value={r.quantity} max={maxWhQty} colorClass="bg-amber-500" /></td>
                      </tr>
                    ))}
                    {byWarehouse.length === 0 && (
                      <tr><td colSpan="4" className="p-6 text-center text-slate-500">{loading ? 'Loading...' : 'No data'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* BY ROLE */}
          <Card data-testid="breakdown-role">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-600" />
                By Target Role
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-medium">Role</th>
                      <th className="text-right p-2 text-xs font-medium">Movements</th>
                      <th className="text-right p-2 text-xs font-medium">Quantity</th>
                      <th className="p-2 w-24 text-xs font-medium">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byRole.map((r) => (
                      <tr key={r.to_holder_type} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-medium">{roleLabel(r.to_holder_type)}</td>
                        <td className="p-2 text-right">{r.movements}</td>
                        <td className="p-2 text-right font-semibold">{r.quantity}</td>
                        <td className="p-2"><MiniBar value={r.quantity} max={maxRoleQty} colorClass="bg-indigo-500" /></td>
                      </tr>
                    ))}
                    {byRole.length === 0 && (
                      <tr><td colSpan="4" className="p-6 text-center text-slate-500">{loading ? 'Loading...' : 'No data'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* OVER TIME */}
          <Card data-testid="breakdown-time">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-teal-600" />
                Over Time ({groupBy})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-medium">Bucket</th>
                      <th className="text-right p-2 text-xs font-medium">Movements</th>
                      <th className="text-right p-2 text-xs font-medium">Quantity</th>
                      <th className="p-2 w-24 text-xs font-medium">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTime.map((r) => (
                      <tr key={r.bucket} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-medium font-mono text-xs">{r.bucket}</td>
                        <td className="p-2 text-right">{r.movements}</td>
                        <td className="p-2 text-right font-semibold">{r.quantity}</td>
                        <td className="p-2"><MiniBar value={r.quantity} max={maxTimeQty} colorClass="bg-teal-500" /></td>
                      </tr>
                    ))}
                    {byTime.length === 0 && (
                      <tr><td colSpan="4" className="p-6 text-center text-slate-500">{loading ? 'Loading...' : 'No data'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* BY ITEM TYPE (bifurcation — Round 77) */}
        <Card data-testid="breakdown-item-type">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4 text-purple-600" />
              By Item Type
              <span className="ml-2 text-xs font-normal text-slate-500">
                (PPS · PPQR · Pocket QR · SoundBox · POS etc.)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs font-medium">Item Type</th>
                    <th className="text-right p-2 text-xs font-medium">Movements</th>
                    <th className="text-right p-2 text-xs font-medium">Quantity Distributed</th>
                    <th className="p-2 w-32 text-xs font-medium">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {byItemType.map((r) => (
                    <tr key={r.item_type_id} className="border-b hover:bg-slate-50">
                      <td className="p-2 font-medium">{r.item_type_name}</td>
                      <td className="p-2 text-right">{r.movements}</td>
                      <td className="p-2 text-right font-semibold text-purple-700">{(r.quantity || 0).toLocaleString()}</td>
                      <td className="p-2"><MiniBar value={r.quantity} max={maxItemTypeQty} colorClass="bg-purple-500" /></td>
                    </tr>
                  ))}
                  {byItemType.length === 0 && (
                    <tr><td colSpan="4" className="p-6 text-center text-slate-500">{loading ? 'Loading...' : 'No data'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* PIVOT: Item Type × City/Role (Round 78) */}
        <Card data-testid="pivot-table">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-3 flex-wrap">
              <Package className="h-4 w-4 text-indigo-600" />
              <span>Pivot: Item Type ×</span>
              <Select value={pivotDim} onValueChange={setPivotDim}>
                <SelectTrigger className="w-32 h-7 text-xs" data-testid="pivot-dim"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="city">City</SelectItem>
                  <SelectItem value="role">Target Role</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs font-normal text-slate-500">
                (each cell = quantity distributed)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const pivotSrc = pivotDim === 'city' ? pivotTypeCity : pivotTypeRole;
              const itemTypes = Object.keys(pivotSrc);
              // Union of column keys, sorted by column-total desc
              const colTotals = {};
              itemTypes.forEach((it) => {
                Object.entries(pivotSrc[it] || {}).forEach(([col, val]) => {
                  colTotals[col] = (colTotals[col] || 0) + val;
                });
              });
              const cols = Object.keys(colTotals).sort((a, b) => colTotals[b] - colTotals[a]);
              if (itemTypes.length === 0) {
                return <p className="text-sm text-slate-500 text-center py-6">{loading ? 'Loading...' : 'No data'}</p>;
              }
              // Row totals
              const rowTotal = (it) => Object.values(pivotSrc[it] || {}).reduce((s, v) => s + v, 0);
              const sortedRows = [...itemTypes].sort((a, b) => rowTotal(b) - rowTotal(a));
              return (
                <div className="overflow-x-auto max-h-[420px]">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left p-2 text-xs font-medium sticky left-0 bg-slate-50 border-b">Item Type ↓ / {pivotDim === 'city' ? 'City' : 'Role'} →</th>
                        {cols.map((c) => (
                          <th key={c} className="text-right p-2 text-xs font-medium border-b whitespace-nowrap">
                            {pivotDim === 'role' ? (ROLE_LABELS[c] || c) : c}
                          </th>
                        ))}
                        <th className="text-right p-2 text-xs font-bold border-b border-l-2 border-indigo-300 bg-indigo-50">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((it) => {
                        const rTot = rowTotal(it);
                        return (
                          <tr key={it} className="border-b hover:bg-slate-50">
                            <td className="p-2 font-medium sticky left-0 bg-white">{it}</td>
                            {cols.map((c) => {
                              const val = (pivotSrc[it] || {})[c] || 0;
                              return (
                                <td key={c} className={`p-2 text-right ${val > 0 ? 'text-slate-900' : 'text-slate-300'}`}>
                                  {val || '—'}
                                </td>
                              );
                            })}
                            <td className="p-2 text-right font-bold text-indigo-700 bg-indigo-50 border-l-2 border-indigo-300">{rTot}</td>
                          </tr>
                        );
                      })}
                      {/* Column totals */}
                      <tr className="bg-slate-100 border-t-2 border-slate-300">
                        <td className="p-2 font-bold text-xs">Total</td>
                        {cols.map((c) => (
                          <td key={c} className="p-2 text-right font-semibold">{colTotals[c]}</td>
                        ))}
                        <td className="p-2 text-right font-bold text-indigo-700 bg-indigo-100">
                          {Object.values(colTotals).reduce((s, v) => s + v, 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* DETAIL ROWS */}
        <Card data-testid="detail-rows">
          <CardHeader>
            <CardTitle className="text-base">
              Recent Movements
              <span className="ml-2 text-xs font-normal text-slate-500">
                (showing {rows.length} of {rowsTotal})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Date</th>
                    <th className="text-left p-2 font-medium">Type</th>
                    <th className="text-left p-2 font-medium">Item</th>
                    <th className="text-left p-2 font-medium">Serial #</th>
                    <th className="text-right p-2 font-medium">Qty</th>
                    <th className="text-left p-2 font-medium">From</th>
                    <th className="text-left p-2 font-medium">To</th>
                    <th className="text-left p-2 font-medium">City</th>
                    <th className="text-left p-2 font-medium">Warehouse</th>
                    <th className="text-left p-2 font-medium">By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-slate-50">
                      <td className="p-2 whitespace-nowrap text-slate-600">
                        {r.effective_date ? new Date(r.effective_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-2">
                        <span className={`px-1.5 py-0.5 rounded ${
                          r.movement_type === 'deploy' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>{r.movement_type}</span>
                      </td>
                      <td className="p-2">{r.item_type_name || '-'}</td>
                      <td className="p-2 font-mono text-indigo-700">{r.serial_number || '-'}</td>
                      <td className="p-2 text-right font-semibold">{r.quantity}</td>
                      <td className="p-2">
                        <div>{r.from_holder_name || '-'}</div>
                        <div className="text-slate-400 text-[10px] uppercase">{roleLabel(r.from_holder_type)}</div>
                      </td>
                      <td className="p-2">
                        <div>{r.to_holder_name || '-'}</div>
                        <div className="text-slate-400 text-[10px] uppercase">{roleLabel(r.to_holder_type)}</div>
                      </td>
                      <td className="p-2">{r.city || '-'}</td>
                      <td className="p-2">{r.source_warehouse_name || '-'}</td>
                      <td className="p-2 text-slate-600">{r.triggered_by_user_name || '-'}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan="10" className="p-8 text-center text-slate-500">
                        {loading ? 'Loading...' : 'No movements in the selected range'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DistributionAnalytics;
